import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  THEME_ATTRIBUTE,
  THEME_STORAGE_KEY,
  ThemeContext,
  type ResolvedTheme,
  type ThemeContextValue,
  type ThemePreference,
} from './ThemeContext';

const DARK_QUERY = '(prefers-color-scheme: dark)';

function isPreference(value: string | null): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

/** The stored preference, or `system` when there is none or storage is closed. */
export function readStoredPreference(): ThemePreference {
  try {
    const stored = globalThis.localStorage.getItem(THEME_STORAGE_KEY);
    return isPreference(stored) ? stored : 'system';
  } catch {
    // Private browsing and locked-down enterprise profiles both throw here.
    // A theme is not worth failing to render over.
    return 'system';
  }
}

function systemTheme(): ResolvedTheme {
  return globalThis.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

/**
 * Holds the theme and keeps the document in step with it.
 *
 * The resolved theme is written to `data-theme` on the root element, which is
 * the single selector the whole stylesheet keys off. Nothing else — no class on
 * a wrapper, no inline styles — so a screenshot, a print stylesheet and a test
 * all see the same thing.
 *
 * `system` is followed live: a user who switches their OS to dark at dusk sees
 * the app follow without reloading, because the media-query listener stays
 * attached rather than being read once at startup.
 *
 * @param children the application
 * @returns the provider
 */
export function ThemeProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [system, setSystem] = useState<ResolvedTheme>(systemTheme);

  useEffect(() => {
    const media = globalThis.matchMedia(DARK_QUERY);
    function handleChange(event: MediaQueryListEvent): void {
      setSystem(event.matches ? 'dark' : 'light');
    }
    media.addEventListener('change', handleChange);
    return () => {
      media.removeEventListener('change', handleChange);
    };
  }, []);

  const resolved: ResolvedTheme = preference === 'system' ? system : preference;

  useEffect(() => {
    document.documentElement.setAttribute(THEME_ATTRIBUTE, resolved);
  }, [resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      globalThis.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // See readStoredPreference: storage may be unavailable. The choice still
      // applies for this session; it just will not survive a reload.
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext value={value}>{children}</ThemeContext>;
}
